import { defineParams } from "@sveltejs/kit/params";
// Kit imports this file with plain Node at build time, so imports need their real extension.
import { selectAreas } from "#lib/utils/areaUtils.ts";
import { isDayViewSlug } from "#lib/utils/spotprisenViews.ts";

/**
 * Area segment on /stromavtale/[slug]. Only the non-default variant is
 * addressable: the bare URL is Nord-Norge, `/sor` is everything else.
 * A `nord` slug would render the same page as the bare URL, so it is
 * deliberately not accepted.
 */
const matchAvtaleOmraade = (param: string) => param === "sor";

/**
 * Bindingstid-segment på /stromavtale/[slug]. Kun det ikke-default valget er
 * adresserbart: bar URL er 1 år, `/3` er 3 år (speiler nord/sor på områdevelgeren).
 * En `1`-verdi ville rendret samme side som bar URL, så den godtas ikke.
 */
const matchFastprisBinding = (param: string) => param === "3";

/**
 * Matcher for the historikk route's rest param. Validates an ordered chain of
 * optional segments: `[year]/[area]/[presentation]` where each is independently
 * optional but order is enforced.
 *
 * Year is a 4-digit integer in [HISTORIKK_MIN_YEAR, currentYear]. The lower
 * bound matches `_meta.ts` HISTORIKK_YEARS — bump in both places if extending.
 *
 * Valid: `""`, `"2024"`, `"nord-norge"`, `"tabell"`, `"2024/nord-norge"`,
 *        `"2024/tabell"`, `"nord-norge/tabell"`, `"2024/nord-norge/tabell"`.
 * Invalid: out-of-order segments, unknown values, out-of-range years.
 */
const PRESENTATIONS = ["tabell"];

const YEAR_RE = /^\d{4}$/;
const HISTORIKK_MIN_YEAR = 2022;

export const matchHistorikkPath = (param: string) => {
	if (!param) return true;

	// See spotprisenPath.ts — strip empty segments so a trailing slash
	// doesn't fail the strict `i === parts.length` check.
	const parts = param.split("/").filter(Boolean);

	let i = 0;

	if (parts[i] && YEAR_RE.test(parts[i])) {
		const y = Number(parts[i]);

		if (y < HISTORIKK_MIN_YEAR || y > new Date().getFullYear()) return false;

		i++;
	}

	if (i < parts.length && selectAreas.some((a) => a.seoSlug === parts[i])) i++;
	if (i < parts.length && PRESENTATIONS.includes(parts[i])) i++;

	return i === parts.length;
};

/**
 * Constrains the [[lang]] optional param to non-default languages only.
 * Default Norwegian (nb) carries no URL segment. Without this matcher,
 * [[lang]] would silently accept any value and could swallow path
 * segments meant for sibling routes like `spotprisen`, `nettbutikk`, etc.
 */
const matchLang = (param: string) => ["en", "se"].includes(param);

/**
 * Matcher for the spotprisen route's rest param. Validates an ordered chain of
 * optional segments: `[view]/[area]/[presentation]` where each is independently
 * optional but order is enforced.
 *
 * Valid: `""`, `"i-gar"`, `"nord-norge"`, `"tabell"`,
 *        `"i-gar/nord-norge"`, `"nord-norge/tabell"`,
 *        `"i-gar/nord-norge/tabell"`.
 * Invalid: anything with segments out of order, duplicates, or unknown values.
 */
const PRESENTATIONS2 = ["tabell"];

export const matchSpotprisenPath = (param: string) => {
	if (!param) return true;

	// `filter(Boolean)` so a trailing slash (or repeated slashes) doesn't
	// leave an empty segment that fails the `i === parts.length` check —
	// Sanity Presentation's locations resolver hrefs are `/spotprisen/{slug}/`.
	const parts = param.split("/").filter(Boolean);

	let i = 0;

	if (isDayViewSlug(parts[i])) i++;
	if (i < parts.length && selectAreas.some((a) => a.seoSlug === parts[i])) i++;
	if (i < parts.length && PRESENTATIONS2.includes(parts[i])) i++;

	return i === parts.length;
};

export const params = defineParams({
	avtaleOmraade: (param) => (matchAvtaleOmraade(param) ? param : undefined),
	fastprisBinding: (param) => (matchFastprisBinding(param) ? param : undefined),
	historikkPath: (param) => (matchHistorikkPath(param) ? param : undefined),
	lang: (param) => (matchLang(param) ? param : undefined),
	spotprisenPath: (param) => (matchSpotprisenPath(param) ? param : undefined),
});
